import { ActionButton, OfficeHeader, Paginator } from "components";
import { FC, useMemo } from "react";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import { GetAllPointRedemptionUsages_getAllPointRedemptionUsages } from "_graphql/queries/__generated__/getAllPointRedemptionUsages";
import Table from "components/tables/table";
import UpdatePointRedemptionContainer from "./update";
import { UserCircleIcon } from "@heroicons/react/24/outline";
import ViewPointRedemptionContainer from "./view";
import config from "config";
import { usePagination } from "hooks";
import { usePointRedemptionUsages } from "hooks/data/use-point-redemptions";
import { withPermissions } from "utils";

const PointRedemptionsPage: FC = () => {
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();
  const { offset, limit, setPage } = usePagination();

  const { redemptions, loading, refetch } = usePointRedemptionUsages({
    brokerId: config.brokerId,
  });

  const length = useMemo(() => redemptions?.length || 0, [redemptions]);

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <Table
            title="Point Redemptions"
            loading={loading}
            hasSearch
            columns={[
              {
                name: "Redeemed By",
                accessor: "redeemed_by",
                render: (
                  item: GetAllPointRedemptionUsages_getAllPointRedemptionUsages
                ) => (
                  <div className="flex items-center space-x-2">
                    <UserCircleIcon className="h-6 w-6" />
                    <div className="flex-1 flex flex-col">
                      <span>
                        {item.redeemed_by?.assoc_first_name}{" "}
                        {item.redeemed_by?.assoc_last_name}
                      </span>
                      <span className="font-light text-xs">
                        {item.redeemed_by?.assoc_email}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Prize",
                accessor: "prize",
                render: (
                  item: GetAllPointRedemptionUsages_getAllPointRedemptionUsages
                ) => (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 flex flex-col">
                      <span>{item.prize}</span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Status",
                accessor: "status",
                render: (
                  item: GetAllPointRedemptionUsages_getAllPointRedemptionUsages
                ) => (
                  <div className="flex items-center">
                    <span
                      className={`inline-block px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                        item.status === "REDEEMED"
                          ? "bg-green-100 text-green-800"
                          : item.status === "PENDING"
                            ? "bg-yellow-100 text-yellow-800"
                            : item.status === "IN_PROGRESS"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                ),
              },
              {
                name: "Voucher",
                accessor: "voucher",
                render: (
                  item: GetAllPointRedemptionUsages_getAllPointRedemptionUsages
                ) => (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 flex flex-col">
                      <span>{item.voucher}</span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Date",
                accessor: "created_at",
                render: (
                  item: GetAllPointRedemptionUsages_getAllPointRedemptionUsages
                ) => (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 flex flex-col">
                      <span>
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Actions",
                accessor: "id",
                render: (item: { id: any }) => (
                  <>
                    <ActionButton
                      action="view"
                      onClick={dispatchAction(item?.id || "", "view")}
                    />
                    {withPermissions([
                      "*:*",
                      "place-it:manage_points_redemptions",
                    ])(
                      <ActionButton
                        action="update"
                        onClick={dispatchAction(item?.id || "", "update")}
                      />
                    )}
                  </>
                ),
              },
            ]}
            data={redemptions}
            refetch={refetch}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={length}
                currentSize={redemptions.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>

      {searchParams?.id && (
        <>
          <ViewPointRedemptionContainer
            open={searchParams?.modal === "view"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "view" : undefined,
                  };
                },
              });
            }}
          />

          <UpdatePointRedemptionContainer
            open={searchParams?.modal === "update"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "update" : undefined,
                  };
                },
              });
            }}
          />
        </>
      )}
    </main>
  );
};

export default PointRedemptionsPage;
